import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2 } from "lucide-react";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { trackFormSubmit } from "../lib/tracking";
import { useSEO, breadcrumbSchema, SITE_URL } from "../lib/seo";

const SERVICES = [
  "SIL",
  "SDA",
  "STA",
  "MTA",
  "Community Participation",
  "Personal Care",
  "Daily Living Support",
  "Support Coordination handover",
];
const STATES = ["VIC", "WA"];

export default function CoordinatorFormPage() {
  useSEO({
    title: "Coordinator Referral | Complex NDIS Placements | Alpha Community Care",
    description: "Support Coordinator and provider referral form — fast, compliant NDIS intake acknowledged within 2 business hours. SIL, SDA, forensic overlay, community nursing, MTA and STA.",
    keywords: ["support coordinator referral", "coordinator NDIS referral form", "SIL referral coordinator", "complex NDIS placement", "forensic disability referral"],
    canonical: `${SITE_URL}/referrals/coordinator`,
    jsonLd: [breadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Referrals", path: "/referrals" },
      { name: "Coordinator referral", path: "/referrals/coordinator" },
    ])],
  });
  const [form, setForm] = useState({
    coordinator_name: "",
    organisation: "",
    email: "",
    phone: "",
    participant_name: "",
    participant_state: "",
    participant_suburb: "",
    ndis_number: "",
    services_needed: [],
    plan_start: "",
    plan_end: "",
    urgency: "medium",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const toggle = (s) => setForm((f) => ({
    ...f,
    services_needed: f.services_needed.includes(s) ? f.services_needed.filter((x) => x !== s) : [...f.services_needed, s],
  }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.coordinator_name || !form.email || !form.phone || !form.participant_name || !form.participant_state) {
      toast.error("Please complete all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/referrals/coordinator", form);
      trackFormSubmit("coordinator_referral", { state: form.participant_state, urgency: form.urgency });
      setDone(true);
      toast.success("Referral received. You'll get a response within 2 business hours.");
    } catch (e) {
      toast.error("Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) return (
    <div className="alpha-container py-20 max-w-xl text-center" data-testid="coordinator-form-success">
      <CheckCircle2 className="w-14 h-14 text-[color:var(--alpha-purple-700)] mx-auto" />
      <h1 className="mt-4 text-3xl font-display font-semibold">Referral received.</h1>
      <p className="mt-3 text-[color:var(--alpha-mute)]">Expect a detailed response from our intake team within 2 business hours.</p>
      <Link to="/" className="alpha-btn-ghost mt-6 inline-flex text-sm">Back to home</Link>
    </div>
  );

  return (
    <div data-testid="page-coordinator-form">
      <section className="alpha-gradient-hero border-b border-[color:var(--alpha-line)]">
        <div className="alpha-container pt-10 pb-8">
          <Link to="/referrals" className="inline-flex items-center gap-2 text-sm text-[color:var(--alpha-mute)] hover:text-[color:var(--alpha-purple-700)]">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
          <div className="alpha-overline mt-4">Coordinator / provider referral</div>
          <h1 className="mt-3 text-3xl sm:text-4xl font-display font-semibold tracking-tight">Fast, compliant intake.</h1>
          <p className="mt-3 text-[color:var(--alpha-mute)] max-w-2xl">Built for how you work. We'll review, respond, and confirm capacity within 2 business hours.</p>
        </div>
      </section>

      <section className="alpha-container py-12 max-w-3xl">
        <form onSubmit={submit} className="alpha-card p-6 sm:p-10 space-y-6" data-testid="coordinator-form">
          <h3 className="font-display text-lg font-semibold">Your details</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-semibold">Coordinator name *</label>
              <Input className="mt-2 h-11" value={form.coordinator_name} onChange={(e) => setForm({ ...form, coordinator_name: e.target.value })} required data-testid="c-form-name" />
            </div>
            <div>
              <label className="text-sm font-semibold">Organisation</label>
              <Input className="mt-2 h-11" value={form.organisation} onChange={(e) => setForm({ ...form, organisation: e.target.value })} data-testid="c-form-org" />
            </div>
            <div>
              <label className="text-sm font-semibold">Email *</label>
              <Input type="email" className="mt-2 h-11" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="c-form-email" />
            </div>
            <div>
              <label className="text-sm font-semibold">Phone *</label>
              <Input className="mt-2 h-11" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required data-testid="c-form-phone" />
            </div>
          </div>

          <h3 className="font-display text-lg font-semibold pt-2">Participant details</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-semibold">Participant name *</label>
              <Input className="mt-2 h-11" value={form.participant_name} onChange={(e) => setForm({ ...form, participant_name: e.target.value })} required data-testid="c-form-pname" />
            </div>
            <div>
              <label className="text-sm font-semibold">State *</label>
              <Select value={form.participant_state || "any"} onValueChange={(v) => setForm({ ...form, participant_state: v === "any" ? "" : v })}>
                <SelectTrigger className="mt-2 h-11" data-testid="c-form-state"><SelectValue placeholder="Select state" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">— Select —</SelectItem>
                  {STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-semibold">Suburb</label>
              <Input className="mt-2 h-11" value={form.participant_suburb} onChange={(e) => setForm({ ...form, participant_suburb: e.target.value })} data-testid="c-form-suburb" />
            </div>
            <div>
              <label className="text-sm font-semibold">NDIS number</label>
              <Input className="mt-2 h-11" value={form.ndis_number} onChange={(e) => setForm({ ...form, ndis_number: e.target.value })} data-testid="c-form-ndis" />
            </div>
            <div>
              <label className="text-sm font-semibold">Plan start</label>
              <Input type="date" className="mt-2 h-11" value={form.plan_start} onChange={(e) => setForm({ ...form, plan_start: e.target.value })} data-testid="c-form-start" />
            </div>
            <div>
              <label className="text-sm font-semibold">Plan end</label>
              <Input type="date" className="mt-2 h-11" value={form.plan_end} onChange={(e) => setForm({ ...form, plan_end: e.target.value })} data-testid="c-form-end" />
            </div>
            <div>
              <label className="text-sm font-semibold">Urgency</label>
              <Select value={form.urgency} onValueChange={(v) => setForm({ ...form, urgency: v })}>
                <SelectTrigger className="mt-2 h-11" data-testid="c-form-urgency"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High / Crisis</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <label className="text-sm font-semibold">Services required</label>
            <div className="mt-3 grid sm:grid-cols-2 gap-2">
              {SERVICES.map((s) => (
                <label key={s} className="flex items-center gap-3 p-3 rounded-xl border border-[color:var(--alpha-line)] hover:bg-[color:var(--alpha-soft)] cursor-pointer">
                  <Checkbox checked={form.services_needed.includes(s)} onCheckedChange={() => toggle(s)} data-testid={`c-form-svc-${s.toLowerCase().slice(0, 6).replace(/\s+/g, "-")}`} />
                  <span className="text-sm">{s}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="text-sm font-semibold">Notes & context</label>
            <Textarea className="mt-2" rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Brief background, behavioural support needs, current supports, preferences…" data-testid="c-form-message" />
          </div>

          <button type="submit" disabled={submitting} className="alpha-btn-primary" data-testid="c-form-submit">
            {submitting ? "Sending…" : <>Submit referral <ArrowRight className="w-4 h-4" /></>}
          </button>
        </form>
      </section>
    </div>
  );
}
